import {merge} from 'lodash';
import {inRangeEndInclusive as inRange} from '../../helpers/assessments/inRange';
import AssessmentResult from '../../../values/AssessmentResult';
import Assessment from '../assessment';
import {MAIN_CONTENT_POINTS, TEXT_PARAGRAPH_TOO_LONG_ID} from '@axyseo/const/analysis';

/**
 * Represents the assessment that will look if the text has too long paragraphs.
 */
export default class ParagraphTooLongAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {object} config       The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: TEXT_PARAGRAPH_TOO_LONG_ID,
      priority: 'high',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-on-page-audit/checklist#paragraphs-off-target',
      fixPosition: 'longParagraph',
      ctaType: 'fix',
      parameters: {
        recommendedLength: 150,
        maximumRecommendedLength: 150
      },
      title: 'Paragraph length',
      content: {
        bad: 'Paragraph too long. Keep paragraph length less than 150 words for readability.',
        improve: '',
        good: 'Paragraph is optimized, with less than 150 words.'
      }
    };

    this.identifier = TEXT_PARAGRAPH_TOO_LONG_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Check if there is language-specific config, and if so, overwrite the current config with it.
   *
   * @param {Researcher} researcher The researcher to use.
   *
   * @returns {Object} The config that should be used.
   */
  getConfig(researcher) {
    const currentConfig = this._config;
    const languageSpecificConfig = researcher.getConfig('paragraphLength');

    /*
     * If a language has a specific paragraph length config, check further if the assessment is run in product pages.
     * If it's run in product pages, override the default config parameters with the language specific config for product pages,
     * otherwise override it with the language specific config for default pages analysis.
     */
    if (languageSpecificConfig) {
      currentConfig.parameters = this._isProduct
        ? languageSpecificConfig.productPageParams
        : languageSpecificConfig.defaultPageParams;
    }

    return currentConfig;
  }

  /**
   *
   * @param paragraphsLength
   * @param config
   * @param hasDescription
   * @returns {{score: number, status: (string)}}
   */
  calculateResult({paragraphsLength, config, hasDescription}) {
    let status = 'bad';
    if (paragraphsLength.length === 0 || !hasDescription) {
      status = 'bad';
    } else {
      const { recommendedLength, maximumRecommendedLength } = config.parameters;
      const length = paragraphsLength[0].countLength;

      const isGood =
        length <= recommendedLength ||
        inRange(length, recommendedLength, maximumRecommendedLength);

      status = isGood ? 'good' : 'bad';
    }
    const score = this.getScore(MAIN_CONTENT_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   * Sort the paragraphs based on word count.
   *
   * @param {Array} paragraphs The array with paragraphs.
   *
   * @returns {Array} The array sorted on word counts.
   */
  sortParagraphs(paragraphs) {
    return paragraphs.sort(function(a, b) {
      return b.countLength - a.countLength;
    });
  }

  /**
   * Runs the getParagraphLength module, based on this returns an assessment result with score and text.
   *
   * @param {Paper} paper             The paper to use for the assessment.
   * @param {Researcher} researcher   The researcher used for calling research.
   * @returns {object} The assessment result.
   */
  getResult({paper, researcher}) {
    let paragraphsLength = researcher.getResearch('getParagraphLength');

    paragraphsLength = this.sortParagraphs(paragraphsLength);
    const config = this.getConfig(researcher);
    const hasDescription = paper.hasDescription();
    const paragraphLengthResult = this.calculateResult({
      paragraphsLength,
      config,
      hasDescription
    });
    const assessmentResult = new AssessmentResult({config: this._config});

    assessmentResult.setScore(paragraphLengthResult.score);
    assessmentResult.setStatus(paragraphLengthResult.status);

    return assessmentResult;
  }

  /**
   * Checks if the paragraphTooLong assessment is applicable to the paper.
   *
   * @param {Paper} paper The paper to check.
   *
   * @returns {boolean} Returns true if the assessment is applicable to the paper.
   */
  isApplicable(paper) {
    return true;
  }
}
